import React, { Component } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { HashRouter, Route, Switch } from "react-router-dom";
import { Toast } from "devextreme-react/toast";
import { Button, Container } from "reactstrap";
import {
  getParameter,
  getParameterSets,
} from "./redux/parameters/parameterActions";
import {
  getProducts,
  getProductsGroups,
  getProductsCategory,
  getBrandsStart,
  getSuppliersStart,
  getTotalCount,
  getAssortments,
  getVatRates,
  getProductGroupTotal,
  getPriorityGroup,
  getProductCountries,
  getCompanyTypes,
  getCurrencies,
  getSupplierTypes,
  getIndentedCategories,
  getIndentedGroups,
} from "./redux/products/productsActions";
import {
  saveAppKeys,
  saveBaseUrl,
  getAccountConfigStart,
  getUserRights,
  getUserLangs,
  getSessionUser,
  getFranchiseInfo,
} from "./redux/appKeys/appKeyActions";
import { selectProductList } from "./redux/products/productsSelectors";
import { getTranslation, requestERPLYData } from "./utils";
import * as serviceWorker from "./serviceWorker";
import "devextreme/dist/css/dx.common.css";
import "devextreme/dist/css/dx.light.css";
import "./App.scss";
import deMessages from "devextreme/localization/messages/de.json";
import ruMessages from "devextreme/localization/messages/ru.json";
import csMessages from "devextreme/localization/messages/cs.json";
import elMessages from "devextreme/localization/messages/el.json";
import esMessages from "devextreme/localization/messages/es.json";
import fiMessages from "devextreme/localization/messages/fi.json";
import jaMessages from "devextreme/localization/messages/ja.json";
import frMessages from "devextreme/localization/messages/fr.json";
import huMessages from "devextreme/localization/messages/hu.json";
import itMessages from "devextreme/localization/messages/it.json";
import nlMessages from "devextreme/localization/messages/nl.json";
import ptMessages from "devextreme/localization/messages/pt.json";
import slMessages from "devextreme/localization/messages/sl.json";
import svMessages from "devextreme/localization/messages/sv.json";
import trMessages from "devextreme/localization/messages/tr.json";
import viMessages from "devextreme/localization/messages/vi.json";
import zhMessages from "devextreme/localization/messages/zh.json";
import { locale, loadMessages } from "devextreme/localization";
import Server from "./service/Server";
import { partnerKey } from "./utils";

// Containers
const DefaultLayout = React.lazy(() => import("./containers/DefaultLayout"));

export let pageLang = "eng";
export let messageCountry = "";

export const loading = (value) => (
  <div className='animated fadeIn pt-3 text-center'>{`${getTranslation(
    value ? value : "loading",
    pageLang
  )}...`}</div>
);

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      newVersionAvailable: false,
      waitingWorker: {},
    };
    loadMessages(deMessages);
    loadMessages(ruMessages);
    loadMessages(csMessages);
    loadMessages(elMessages);
    loadMessages(esMessages);
    loadMessages(fiMessages);
    loadMessages(jaMessages);
    loadMessages(frMessages);
    loadMessages(huMessages);
    loadMessages(itMessages);
    loadMessages(nlMessages);
    loadMessages(ptMessages);
    loadMessages(slMessages);
    loadMessages(svMessages);
    loadMessages(trMessages);
    loadMessages(viMessages);
    loadMessages(zhMessages);
    locale(navigator.language);
  }

  onServiceWorkerUpdate = (registration) => {
    this.setState({
      waitingWorker: registration && registration.waiting,
      newVersionAvailable: true,
    });
  };

  updateServiceWorker = () => {
    const { waitingWorker } = this.state;
    waitingWorker && waitingWorker.postMessage({ type: "SKIP_WAITING" });
    this.setState({ newVersionAvailable: false });
    window.location.reload();
  };

  componentDidMount() {
    /*
    Initialize plugin with the JWT received from ERPLY BO
     */
    this.setState({ loading: true });
    window.parent.postMessage("apiSessionKey", "*");
    window.addEventListener(
      "message",
      (message) => {
        if (
          message.origin.includes("erply.com") &&
          message.data.sender === "erply" &&
          message.data.apiSessionKey
        ) {
          if (message.data.page_lang) {
            pageLang = message.data.page_lang;
          }

          if (message.data.country) {
            messageCountry = message.data.country;
          }

          requestERPLYData({
            clientCode: message.data.clientCode,
            request: "getServiceEndpoints",
            partnerKey,
          })
            .then((res) => {
              // console.log(res.data.records[0]);
              this.setState({ loading: false });
              const request = {
                baseUrl: res.data.records[0].pim.url,
                keys: {
                  clientCode: message.data.clientCode,
                  sessionKey: message.data.apiSessionKey,
                },
              };
              Server.init(request);
              localStorage.setItem("baseURL", res.data.records[0].pim.url);
              localStorage.setItem("jsonURL", res.data.records[0].json.url);

              localStorage.setItem("groupStore", JSON.stringify([]));
              localStorage.setItem("categoryStore", JSON.stringify([]));

              this.props.saveAppKeys({
                loginUrl: message.data.url,
                clientCode: message.data.clientCode,
                sessionKey: message.data.apiSessionKey,
                jwt: message.data.jwt,
                cdnUrl: res.data.records[0].cdn.url,
                jsonURL: res.data.records[0].json.url,
                pageLang,
                cafaUrl: res.data.records[0].cafa.url,
                adminBaseUrl: res.data.records[0]["account-admin"].url,
                inventoryBaseUrl: res.data.records[0].inventory.url,
                crmBaseUrl: res.data.records[0].crm.url,
              });

              this.props.getUserRights({
                clientCode: message.data.clientCode,
                sessionKey: message.data.apiSessionKey,
              });

              this.props.getFranchiseInfo({
                clientCode: message.data.clientCode,
                sessionKey: message.data.apiSessionKey,
                adminBaseUrl: res.data.records[0]["account-admin"].url,
              });

              this.props.getUserLangs({
                clientCode: message.data.clientCode,
                sessionKey: message.data.apiSessionKey,
              });

              this.props.getSessionUser({
                clientCode: message.data.clientCode,
                sessionKey: message.data.apiSessionKey,
              });

              this.props.getSupplierTypes(request);
              this.props.getCompanyTypes({
                clientCode: message.data.clientCode,
                sessionKey: message.data.apiSessionKey,
              });

              this.props.getCurrencies({
                clientCode: message.data.clientCode,
                sessionKey: message.data.apiSessionKey,
              });

              this.props.getAccountConfigStart({
                pageLang,
                clientCode: message.data.clientCode,
                sessionKey: message.data.apiSessionKey,
              });
              this.props.saveBaseUrl(res.data.records[0].pim.url);

              this.props.getVatRates({
                clientCode: message.data.clientCode,
                sessionKey: message.data.apiSessionKey,
              });
              this.props.getIndentedCategories(request);
              this.props.getIndentedGroups(request);
              this.props.getProductsCategory(request);
              this.props.getProductsGroups(request);
              this.props.getBrandsStart(request);
              // this.props.getSuppliersStart(request);
              this.props.getParameter(request);
              this.props.getParameterSets(request);
              this.props.getAssortments(request);
              this.props.getPriorityGroup(request);
              this.props.getProductCountries(request);
            })
            .catch((err) => {
              console.log(err);
              this.setState({ loading: false });
            });
        }
      },
      false
    );

    if (process.env.NODE_ENV === "production") {
      serviceWorker.register({ onUpdate: this.onServiceWorkerUpdate });
    }
  }

  renderToastContent = () => {
    return (
      <Container
        className='text-center'
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <h2 className='mr-4'>{getTranslation("newVersion", pageLang)}</h2>
        <Button onClick={this.updateServiceWorker} className='text-uppercase'>
          {getTranslation("refresh", pageLang)}
        </Button>
      </Container>
    );
  };

  render() {
    return (
      <div>
        {this.state.loading ? (
          loading()
        ) : (
          <HashRouter>
            <React.Suspense fallback={loading()}>
              <Switch>
                <Route
                  path='/'
                  name='Home'
                  render={(props) => <DefaultLayout {...props} />}
                />
              </Switch>
            </React.Suspense>
          </HashRouter>
        )}
        <Toast
          visible={this.state.newVersionAvailable}
          type='info'
          width={"50%"}
          displayTime={200000}
          contentRender={this.renderToastContent}
        />
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  productList: selectProductList,
});

const mapActionsToProps = {
  getParameter,
  getParameterSets,
  getProducts,
  getProductsGroups,
  getProductsCategory,
  getBrandsStart,
  getSuppliersStart,
  getTotalCount,
  getAssortments,
  saveAppKeys,
  saveBaseUrl,
  getVatRates,
  getAccountConfigStart,
  getProductGroupTotal,
  getPriorityGroup,
  getProductCountries,
  getUserRights,
  getCompanyTypes,
  getCurrencies,
  getSupplierTypes,
  getIndentedCategories,
  getIndentedGroups,
  getUserLangs,
  getSessionUser,
  getFranchiseInfo,
};

export default connect(mapStateToProps, mapActionsToProps)(App);
